import {Role, ServiceCenter, Useraccount} from "../../../../../types/domain";
import {Alert, Button, Input, Modal, Select, TextInput} from "@mantine/core";
import React, {useEffect, useState} from "react";
import ServicecenterService from "../../../../../services/servicecenter.service";
import UseraccountService from "../../../../../services/useraccount.service";
import RoleService from "../../../../../services/role.service";
import * as Sentry from "@sentry/react";

type UserModalProps = {
    opened: boolean,
    close: () => void,
    title?: string,
    user: Useraccount | null,
    userRole: Role | null,
    userServiceCenters: Array<ServiceCenter>
};

export function UserModal({
                              opened,
                              close,
                              title,
                              user,
                              userRole,
                              userServiceCenters
                          }: UserModalProps) {
    const [firstnameState, setFirstnameState] = useState("");
    const [lastnameState, setLastnameState] = useState("");
    const [serviceCenterState, setServiceCenterState] = useState("");
    const [serviceCenters, setServiceCenters] = useState<ServiceCenter[]>([]);
    const [roleState, setRoleState] = useState("");
    const [roles, setRoles] = useState<Role[]>([]);

    const [showInputAlert, setShowInputAlert] = useState(false);
    const [showAlert, setShowAlert] = useState(false);

    //Synchronizing
    useEffect(() => {
        async function loadServicecenters() {
            try {
                if (userRole?.name === 'Admin') {
                    const response = await ServicecenterService.getAll();
                    setServiceCenters(response.data);
                } else if (userRole?.name === 'DSS Leiter*in') {
                    setServiceCenters(userServiceCenters);
                }
            } catch (e) {
                console.error("Error while reading servicecenters: ", e);
            }
        }

        async function loadRoles() {
            try {
                const roles = await RoleService.getAll();
                if (userRole?.name === 'Admin') {
                    setRoles(roles.data.filter(r => r.name !== 'Admin'));
                } else if (userRole?.name === 'DSS Leiter*in') {
                    setRoles(roles.data.filter(r => r.name !== 'DSS Leiter*in' && r.name !== 'Admin'));
                }
            } catch (e) {
                console.error("Error while reading roles: ", e);
            }
        }

        loadServicecenters();
        loadRoles();

        if (user) {
            setFirstnameState(user.firstname ?? "");
            setLastnameState(user.lastname ?? "");
            setServiceCenterState(user.serviceCenters.at(0)?.id.toString() ?? "");
            setRoleState(user.role?.id.toString() ?? "");
        } else {
            // New participant
            setFirstnameState("");
            setLastnameState("");
            setServiceCenterState("");
            setRoleState("");
        }

        setShowInputAlert(false);
        setShowAlert(false);
    }, [user, opened, userRole, userServiceCenters]);

    async function updateUser() {
        if (!lastnameState.trim() || !firstnameState.trim() || !serviceCenterState.trim() || !roleState.trim()) {
            setShowInputAlert(true);
            return;
        }

        setShowInputAlert(false);
        if (user) {
            const selectedSC = serviceCenters.find(sc => sc.id.toString() === serviceCenterState);
            const service: ServiceCenter[] = selectedSC ? [selectedSC] : [];
            const selectedRole = roles.find(r => r.id.toString() === roleState);
            const role = selectedRole ? selectedRole : null;

            const updatedUser: Useraccount = {
                ...user,
                firstname: firstnameState,
                lastname: lastnameState,
                serviceCenters: service,
                role: role
            }

            await UseraccountService.update(updatedUser, updatedUser.id)
                .catch((e: Error) => {
                    Sentry.captureException(e, {
                        extra: {
                            request: {
                                user: JSON.parse(JSON.stringify(updatedUser)),
                            },
                        },
                    });
                    setShowAlert(true);
                    close();
                    return;
                });

            close();
        } else {
            setShowAlert(true);
        }
    }

    async function addUser() {
        if (!lastnameState.trim() || !firstnameState.trim() || !serviceCenterState.trim() || !roleState.trim()) {
            setShowInputAlert(true);
            return;
        }

        setShowInputAlert(false);

        const selectedSC = serviceCenters.find(sc => sc.id.toString() === serviceCenterState);
        const service: ServiceCenter[] = selectedSC ? [selectedSC] : [];
        const selectedRole = roles.find(r => r.id.toString() === roleState);
        const role = selectedRole ? selectedRole : null;

        const u: Useraccount = {
            id: 0,
            username: "",
            firstname: firstnameState,
            lastname: lastnameState,
            serviceCenters: service,
            active: true,
            role: role,
            password: ""
        };


        await UseraccountService.create(u)
            .catch((e: Error) => {
                Sentry.captureException(e, {
                    extra: {
                        request: {
                            user: JSON.parse(JSON.stringify(u)),
                        },
                    },
                });
                setShowAlert(true);
                close();
                return;
            });

        close();
    }

    return (
        <Modal opened={opened} onClose={close} centered title={title}>
            <Input.Wrapper>
                <TextInput
                    label="Vorname"
                    mb="sm"
                    value={firstnameState}
                    withAsterisk
                    onChange={(e) => setFirstnameState(e.currentTarget.value)}
                />
                <TextInput
                    label="Nachname"
                    mb="sm"
                    value={lastnameState}
                    withAsterisk
                    onChange={(e) => setLastnameState(e.currentTarget.value)}
                />
                <Select
                    label="Servicestelle"
                    mb="sm"
                    withinPortal
                    data={serviceCenters.map(sc => ({
                        value: sc.id.toString(),
                        label: sc.name
                    }))}
                    value={serviceCenterState}
                    withAsterisk
                    onChange={(value) => {
                        if (value) {
                            setServiceCenterState(value);
                        }
                    }}
                />
                <Select
                    label="Rolle"
                    mb="sm"
                    data={roles.map(r => ({
                        value: r.id.toString(),
                        label: r.name
                    }))}
                    value={roleState}
                    withAsterisk
                    onChange={(value) => {
                        if (value) {
                            setRoleState(value);
                        }
                    }}
                />
            </Input.Wrapper>
            {
                showInputAlert ?
                    <Alert w={"100%"}
                           title={user ? "Aktualisieren fehlgeschlagen" : "Hinzufügen fehlgeschlagen"}
                           ta={"left"} color="red" mt={"10px"}>
                        Bitte füllen Sie alle Felder aus!
                    </Alert> : <></>
            }
            {
                showAlert ?
                    <Alert w={"100%"}
                           title={user ? "Aktualisieren fehlgeschlagen" : "Hinzufügen fehlgeschlagen"}
                           ta={"left"} color="red" mt={"10px"}>
                        Leider ist ein Fehler während des {user ? "aktualisieren" : "einfügen"} aufgetreten.
                        Versuchen Sie es noch einmal oder kontaktieren Sie die Administratoren.
                    </Alert> : <></>
            }
            <div className="buttonRow">
                <Button onClick={user ? updateUser : addUser} mb="sm">
                    {user ? "Aktualisieren" : "Hinzufügen"}
                </Button>
            </div>
        </Modal>
    );
}
